import { useEffect, useSyncExternalStore, type ComponentProps } from "react";
import { css } from "@linaria/core";
import { generatePrimaryPaletteAdaptive } from "../../../../admin-ui/src/theme/primaryPalette";

import { CLASS_CONSTANTS } from "@/utils/constants";
import { clsx } from "clsx";
import { addPageScript } from "@/utils/dom";
import { useBeeOptions, useInitApi } from "@/store/api/bee.api";
import { Loader } from "../Loader/Loader";
import { useAppContext } from "@/context/AppContext/Context";

import {
  prepareAdditionalCss,
  prepareBaseColors,
  themeReset,
} from "./reset.theme";
import { neutralTheme } from "./neutral.theme";

import { theme as rawTheme } from "./raw";
import { theme as classicTheme } from "./classic";
import { theme as modernTheme } from "./modern";
import { BookingLoadError } from "../BookingLoadError";
import { getWidgetPreviewSettings, subscribeWidgetPreviewSettings } from "@/utils/widgetPreview";
import { useWidgetErrors } from "@/errors/useWidgetErrors";
import { WidgetContainer } from "../WidgetContainer";

const subscribeDemoTheme = (callback: () => void) => {
  window.addEventListener("demo-theme-change", callback);
  return () => window.removeEventListener("demo-theme-change", callback);
};
const getDemoTheme = () => document.body.dataset.demoTheme;

interface ThemeProviderProps extends ComponentProps<"div"> {
  addTheme?: boolean;
  theme?: string;
  skipLoader?: boolean;
}

const themes: Record<string, string[]> = {
  light: rawTheme,
  dark: rawTheme,
  "classic-light": classicTheme,
  "classic-dark": classicTheme,
  "modern-dark": modernTheme,
  "modern-light": modernTheme,
};

export const ThemeProvider = (props: ThemeProviderProps) => {
  const { elementType, componentType, config } = useAppContext();
  // Inline buttons also depend on initialization. Subscribe in every widget so
  // an initialization failure reaches its own error surface even without a loader.
  useInitApi();
  const demoTheme = useSyncExternalStore(
    subscribeDemoTheme,
    getDemoTheme,
    () => undefined,
  );
  const {
    theme: overrideTheme,
    skipLoader = false,
    addTheme = elementType.addTheme ?? true,
    ...restProps
  } = props;

  const previewTheme = useSyncExternalStore(
    subscribeWidgetPreviewSettings,
    getWidgetPreviewSettings,
    () => undefined,
  )?.appearance;

  const options = useBeeOptions((options) => ({
    hasLoaded: !!options,
    theme: (options?.integrationTheme ?? "").trim(),
  }))!;



  const baseTheme = config.demoPage ? demoTheme || options.theme : options.theme;
  const themeFamily = baseTheme.replace(/(?:^|-)(light|dark)$/i, "");
  const selectedTheme = previewTheme
    ? themeFamily ? `${themeFamily}-${previewTheme}` : previewTheme
    : baseTheme;

  if (!addTheme) return elementType.inline ? props.children : <WidgetContainer>{props.children}</WidgetContainer>;

  if (!skipLoader && !options.hasLoaded)
    return elementType.showLoader === false ? null : (
      <LoaderOrError componentType={componentType} />
    );

  return (
    <ThemeWrapper
      componentType={componentType}
      theme={overrideTheme ?? selectedTheme}
      isRelative={elementType.showLoader}
      inline={elementType.inline}
      {...restProps}
    >
      {props.children}
    </ThemeWrapper>
  );
};

const LoaderOrError = (props: { componentType: string }) => {
  const { isError } = useInitApi();
  const errors = useWidgetErrors();

  if (isError && errors) return null;

  if (isError)
    return (
      <ThemeWrapper componentType={props.componentType}>
        <BookingLoadError onRetry={() => undefined} />
      </ThemeWrapper>
    );

  return <Loader />;
};

const CustomStyles = () => {
  const preview = useSyncExternalStore(subscribeWidgetPreviewSettings, getWidgetPreviewSettings, () => undefined);
  const { demoPage, noAdditionalCSS } = useAppContext().config;
  const {
    additionalCss = "",
    primaryColor = "",
    primaryColorContrast = "",
    secondaryColor = "",
    secondaryColorContrast = "",
  } = useBeeOptions((options) => ({
    additionalCss: options?.additionalCss,
    primaryColor: options?.primaryColor,
    secondaryColor: options?.secondaryColor,
    primaryColorContrast: options?.primaryColorContrast,
    secondaryColorContrast: options?.secondaryColorContrast,
  }))!;

  useEffect(() => {
    if (
      !primaryColor &&
      !secondaryColor &&
      !primaryColorContrast &&
      !secondaryColorContrast &&
      !additionalCss
    )
      return;

    // Share the admin's palette calculation instead of whitening every dark-mode control.
    let demoColors = "";
    if (demoPage) {
      const seed = primaryColor || "#5263be";
      const light = generatePrimaryPaletteAdaptive(seed, "light", "#ffffff", "#f0f0f2");
      const dark = generatePrimaryPaletteAdaptive(seed, "dark", "#252429", "#1a191e");
      demoColors = `
        .demo-page[data-demo-theme="light"] {
          --demo-primary: ${light.primary50};
          --demo-action-bg: ${light.primary50};
          --demo-action-text: ${light.primary100};
        }
        .demo-page[data-demo-theme="dark"] {
          --demo-primary: ${dark.primary80};
          --demo-action-bg: ${dark.primary60};
          --demo-action-text: ${dark.primary99};
        }
      `;
    }
    const css = [
      prepareBaseColors({
        primaryColor,
        primaryColorContrast,
        secondaryColor,
        secondaryColorContrast,
      }),
      noAdditionalCSS || preview?.customCss === false ? "" : prepareAdditionalCss(additionalCss),
      demoColors,
    ]
      .join("\n\n")
      .trim();

    // addPageScript intentionally inserts once on customer websites. A preview
    // owns its style element and must also be able to remove previously applied
    // custom CSS when the live setting is disabled.
    const previewStyle = preview ? document.getElementById("websail-styles") : null;
    if (previewStyle) previewStyle.textContent = css;
    else addPageScript(css, "style");
  }, [
    demoPage,
    noAdditionalCSS,
    preview?.customCss,
    additionalCss,
    primaryColor,
    primaryColorContrast,
    secondaryColor,
    secondaryColorContrast,
  ]);

  return null;
};

const ThemeWrapper = (
  props: {
    componentType: string;
    theme?: string;
    isRelative?: boolean;
    inline?: boolean;
  } & ComponentProps<"div">,
) => {
  const { className, componentType, theme, isRelative, inline, ...restProps } = props;
  // Inline buttons retain their intrinsic width; layouts use a block container.
  const Container = inline ? "div" : WidgetContainer;

  const themeOption = theme?.toLowerCase() || "light";

  const colorSchema = themeOption?.toLowerCase().endsWith("dark")
    ? "dark"
    : "light";

  const [lightTheme, darkTheme] =
    themes[themeOption] ||
    themes[themeOption + "-light"] ||
    themes[colorSchema];

  return (
    <Container
      className={clsx(
        CLASS_CONSTANTS.MAIN.CONTAINER,
        `${CLASS_CONSTANTS.MAIN.CONTAINER}-${componentType?.toLowerCase()}`,
        className,
      )}
    >
      <CustomStyles />
      <div
        color-scheme={colorSchema}
        {...restProps}
        className={clsx(
          CLASS_CONSTANTS.MAIN.WEBSAIL,
          CLASS_CONSTANTS.MAIN.THEME(themeOption),
          themeReset,
          neutralTheme,
          {
            [lightTheme]: colorSchema === "light",
            [darkTheme]: colorSchema === "dark",
            [darkColorScheme]: colorSchema === "dark",
            [lightColorScheme]: colorSchema === "light",
            [relativeParentClassName]: isRelative !== false,
          },
        )}
      >
        {props.children}
      </div>
    </Container>
  );
};

const relativeParentClassName = css`
  position: relative;
`;

const darkColorScheme = css`
  color-scheme: dark;
`;

const lightColorScheme = css`
  color-scheme: light;
`;
